import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { track } from '../lib/telemetry';
import { getCopy } from '../lib/experiments';
import {
  ArrowRight, ShieldCheck, Activity, MapPin, DollarSign, Users, Truck,
  BarChart3, CheckCircle2, Sparkles, PlayCircle, Loader2,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Feature = ({ icon: Icon, title, body }) => (
  <div className="group p-8 bg-white border border-slate-200 rounded-xl hover:border-[#002FA7]/40 hover:shadow-[0_8px_24px_-12px_rgba(0,47,167,0.25)] transition-all duration-300">
    <div className="w-12 h-12 rounded-lg bg-[#002FA7]/10 flex items-center justify-center mb-5 group-hover:bg-[#002FA7]/15 transition-colors">
      <Icon className="w-6 h-6 text-[#002FA7]" strokeWidth={2} />
    </div>
    <h3 className="text-lg font-semibold tracking-tight text-slate-900 mb-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>{title}</h3>
    <p className="text-sm leading-relaxed text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{body}</p>
  </div>
);

const StatPill = ({ value, label }) => (
  <div className="px-5 py-3 rounded-lg border border-white/15 bg-white/[0.04] backdrop-blur-sm">
    <div className="text-2xl font-bold text-white" style={{ fontFamily: 'Work Sans, sans-serif' }}>{value}</div>
    <div className="text-[11px] uppercase tracking-widest text-slate-300/80 mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{label}</div>
  </div>
);

const Landing = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [demoLoading, setDemoLoading] = useState(false);
  const ctaExperiment = getCopy('demo-cta') || { variant: 'A', label: 'Try live demo', helper: 'No signup. Resets every 24h.' };
  const pricingExperiment = getCopy('pricing-headline') || { variant: 'A', label: 'Pick a plan. Scale on your terms.', helper: 'Cancel anytime.' };

  // Fire one variant-tagged page_view on first mount so the funnel can slice
  // landing impressions by variant. The TelemetryTracker fires a generic
  // page_view too — this just adds the variant dimension we'll group on.
  useEffect(() => {
    track('page_view', null, { experiment: 'demo-cta', variant: ctaExperiment.variant });
    track('page_view', null, { experiment: 'pricing-headline', variant: pricingExperiment.variant });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startDemo = async () => {
    if (demoLoading) return;
    setDemoLoading(true);
    try {
      track('click', 'demo-start', { source: 'landing-hero', experiment: 'demo-cta', variant: ctaExperiment.variant });
      await axios.post(`${API}/demo/login`, {}, { withCredentials: true });
      // Hard reload so AuthContext re-fetches the new cookie identity
      window.location.assign('/dashboard');
    } catch (err) {
      setDemoLoading(false);
      // eslint-disable-next-line no-alert
      alert(err.response?.data?.detail || 'Could not start demo — please try again or use Sign in.');
    }
  };

  useEffect(() => {
    if (!loading && user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    axios.get(`${API}/pricing/plans`).then((r) => setPlans(r.data)).catch(() => setPlans([]));
  }, []);

  const handlePlanCta = () => {
    track('click', 'demo-start', { source: 'pricing-plan', experiment: 'pricing-headline', variant: pricingExperiment.variant });
    if (user) navigate('/billing');
    else navigate('/start-trial');
  };

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      {/* ============ NAV ============ */}
      <header className="absolute top-0 left-0 right-0 z-30" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3" data-testid="landing-logo">
            <img
              src="/icons/logo-wordmark.png"
              alt="CivilX TMS"
              className="h-11 w-auto select-none"
              draggable="false"
            />
          </div>
          <nav className="hidden md:flex items-center gap-8 text-sm text-white/80" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            <a href="#features" className="hover:text-white transition-colors">Features</a>
            <a href="#how-it-works" className="hover:text-white transition-colors">How it works</a>
            <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>
            <a href="#start-trial" className="hover:text-white transition-colors">Start free trial</a>
          </nav>
          <div className="flex items-center gap-3">
            <Link
              to="/login"
              data-testid="nav-signin-link"
              className="text-sm font-medium text-white/90 hover:text-white px-4 py-2 rounded-md transition-colors"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              Sign in
            </Link>
            <Link
              to="/start-trial"
              data-testid="nav-getstarted-button"
              className="text-sm font-semibold bg-white text-[#0A0E27] hover:bg-slate-100 px-4 py-2 rounded-md transition-all duration-200"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              Start free trial
            </Link>
          </div>
        </div>
      </header>

      {/* ============ HERO ============ */}
      <section
        className="relative min-h-[760px] overflow-hidden"
        style={{
          backgroundImage: 'url(/images/login-hero.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {/* Gradient veil for legibility */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#04081f]/85 via-[#04081f]/65 to-[#04081f]/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#04081f] via-transparent to-transparent" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-44 pb-32">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.07] border border-white/10 backdrop-blur-sm mb-7">
              <Sparkles className="w-3.5 h-3.5 text-[#3D7DFF]" strokeWidth={2.5} />
              <span className="text-xs font-medium tracking-wide text-white/85" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                Built for modern trucking fleets
              </span>
            </div>

            <h1
              className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-[1.05] mb-6"
              style={{ fontFamily: 'Work Sans, sans-serif' }}
            >
              Smarter loads.
              <br />
              <span className="bg-gradient-to-r from-[#3D7DFF] via-[#5B8DFF] to-white bg-clip-text text-transparent">
                Stronger business.
              </span>
            </h1>

            <p
              className="text-lg sm:text-xl text-slate-200/90 leading-relaxed mb-10 max-w-2xl"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              Run your entire fleet from one dashboard — dispatch loads, onboard drivers,
              automate payroll, and see real-time KPIs your bookkeeper will love.
            </p>

            <div className="flex flex-wrap items-center gap-4 mb-14">
              <Link
                to="/start-trial"
                data-testid="hero-cta-signup"
                className="group inline-flex items-center gap-2 px-7 py-3.5 bg-[#002FA7] hover:bg-[#0034BD] text-white rounded-md font-semibold shadow-lg shadow-[#002FA7]/30 transition-all duration-200"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              >
                Start free trial — create your company
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={2.5} />
              </Link>
              <button
                type="button"
                onClick={startDemo}
                disabled={demoLoading}
                data-testid="hero-cta-demo"
                data-variant={ctaExperiment.variant}
                className="group inline-flex items-center gap-2 px-7 py-3.5 bg-white hover:bg-slate-100 text-[#002FA7] rounded-md font-semibold transition-all duration-200 disabled:opacity-70"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              >
                {demoLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" strokeWidth={2.5} />}
                {demoLoading ? 'Loading demo…' : ctaExperiment.label}
              </button>
              <Link
                to="/login"
                data-testid="hero-cta-signin"
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-white/[0.08] hover:bg-white/[0.13] border border-white/20 text-white rounded-md font-semibold backdrop-blur-sm transition-all duration-200"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              >
                Sign in
              </Link>
            </div>
            <p className="text-xs text-slate-300 mb-10 -mt-10 max-w-md" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              {ctaExperiment.helper} The live demo logs you straight into a sandboxed dispatch with 9 loads, 4 drivers, and a paid payroll run.
            </p>

            <div className="flex flex-wrap items-center gap-4 max-w-2xl">
              <StatPill value="4" label="User roles" />
              <StatPill value="100%" label="Multi-tenant" />
              <StatPill value="Weekly" label="Auto payroll" />
              <StatPill value="JWT" label="Secure auth" />
            </div>
          </div>
        </div>
      </section>

      {/* ============ FEATURES ============ */}
      <section id="features" className="relative -mt-16 z-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <Feature
              icon={Truck}
              title="Load dispatch in seconds"
              body="Create loads, assign active drivers, track status from Assigned → In Transit → Delivered, and audit every change."
            />
            <Feature
              icon={Users}
              title="Driver onboarding done right"
              body="Invite drivers by email, collect their employment application + CDL + Medical card, and approve in one click."
            />
            <Feature
              icon={DollarSign}
              title="Payroll on autopilot"
              body="Salary, per-mile, hourly, or percentage — generate accurate weekly payrolls with standing deductions and custom adjustments."
            />
          </div>
        </div>
      </section>

      {/* ============ VALUE PROP STRIP ============ */}
      <section className="py-24 sm:py-32" id="how-it-works">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#002FA7] mb-4" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                Operations · Finance · Compliance
              </p>
              <h2 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 mb-6 leading-[1.1]" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                One platform.
                <br />Zero spreadsheets.
              </h2>
              <p className="text-base text-slate-600 leading-relaxed mb-8 max-w-lg" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                CivilX TMS replaces the patchwork of WhatsApp groups, Excel files and paper applications most carriers
                still rely on. Built multi-tenant from day one — your data is isolated, secure, and yours.
              </p>
              <ul className="space-y-4">
                {[
                  'Real-time analytics: loads delivered, miles, utilization, payroll trends',
                  'Role-based access: Super Admin, Company Admin, Dispatcher, Driver',
                  'CSV exports for accounting and audit teams',
                  'Mobile-first driver experience — current load, history, pay stubs',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#34C759] flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                    <span className="text-sm text-slate-700" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Mock dashboard preview */}
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-[#002FA7]/20 via-[#3D7DFF]/10 to-transparent rounded-3xl blur-2xl" />
              <div className="relative bg-white border border-slate-200 rounded-2xl shadow-xl p-6">
                <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-100">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-red-400" />
                    <div className="w-2 h-2 rounded-full bg-amber-400" />
                    <div className="w-2 h-2 rounded-full bg-emerald-400" />
                  </div>
                  <span className="text-xs text-slate-400" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>civilx-tms.app/analytics</span>
                </div>

                <div className="grid grid-cols-3 gap-3 mb-5">
                  {[
                    { v: '482', l: 'Loads', c: '#002FA7', i: Truck },
                    { v: '94%', l: 'On-time', c: '#34C759', i: Activity },
                    { v: '$74k', l: 'Payroll', c: '#FF9500', i: DollarSign },
                  ].map(({ v, l, c, i: I }) => (
                    <div key={l} className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                      <I className="w-4 h-4 mb-2" style={{ color: c }} strokeWidth={2.5} />
                      <div className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>{v}</div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-0.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{l}</div>
                    </div>
                  ))}
                </div>

                <div className="bg-gradient-to-br from-slate-50 to-white rounded-lg border border-slate-100 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-semibold text-slate-700 uppercase tracking-widest" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Loads delivered · 30d</span>
                    <BarChart3 className="w-4 h-4 text-slate-400" strokeWidth={2} />
                  </div>
                  <svg viewBox="0 0 300 90" className="w-full h-20">
                    <defs>
                      <linearGradient id="lg" x1="0" x2="0" y1="0" y2="1">
                        <stop offset="0%" stopColor="#002FA7" stopOpacity="0.35" />
                        <stop offset="100%" stopColor="#002FA7" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                    <path
                      d="M0,70 C20,60 35,55 55,50 C80,42 95,38 120,28 C145,22 165,30 190,22 C215,16 235,24 260,12 L300,8 L300,90 L0,90 Z"
                      fill="url(#lg)"
                    />
                    <path
                      d="M0,70 C20,60 35,55 55,50 C80,42 95,38 120,28 C145,22 165,30 190,22 C215,16 235,24 260,12 L300,8"
                      fill="none"
                      stroke="#002FA7"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ MORE FEATURES ============ */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mb-14">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#002FA7] mb-4" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              Everything you need
            </p>
            <h2 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 leading-[1.1]" style={{ fontFamily: 'Work Sans, sans-serif' }}>
              Designed for dispatchers, loved by drivers.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            <Feature icon={MapPin} title="Multi-tenant by default" body="Each carrier's data is isolated end-to-end. Add unlimited drivers and dispatchers under one company." />
            <Feature icon={ShieldCheck} title="JWT + RBAC security" body="HTTPOnly cookies, bcrypt password hashing, four-tier role permissions. No surprises." />
            <Feature icon={BarChart3} title="Reports & analytics" body="Track utilization, top performers, payroll trends. Export to CSV for finance." />
            <Feature icon={Activity} title="Live load tracking" body="Drivers update status in one tap. Dispatchers see the live queue in real time." />
            <Feature icon={DollarSign} title="Standing deductions" body="ELD fees, parking, insurance, advances — apply once and they flow into every payroll." />
            <Feature icon={Users} title="Driver applications" body="Tokenized invite links, document uploads (CDL, Medical), admin approval gate." />
          </div>
        </div>
      </section>

      {/* ============ PRICING ============ */}
      <section id="pricing" className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mx-auto text-center mb-14">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#002FA7] mb-4" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              Simple pricing
            </p>
            <h2 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 mb-5 leading-[1.1]" data-variant={pricingExperiment.variant} style={{ fontFamily: 'Work Sans, sans-serif' }}>
              {pricingExperiment.label}
            </h2>
            <p className="text-base text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              {pricingExperiment.helper}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {plans.map((plan) => (
              <div
                key={plan.id}
                data-testid={`landing-plan-${plan.id}`}
                className={`relative bg-white border rounded-2xl p-8 flex flex-col transition-all duration-300 ${plan.popular ? 'border-[#002FA7] shadow-[0_12px_40px_-12px_rgba(0,47,167,0.4)] md:-translate-y-3' : 'border-slate-200 hover:border-slate-300'}`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-[#002FA7] text-white text-xs font-semibold rounded-full" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                    Most popular
                  </div>
                )}
                <h3 className="text-xl font-semibold text-slate-900 mb-1.5" style={{ fontFamily: 'Work Sans, sans-serif' }}>{plan.name}</h3>
                <p className="text-sm text-slate-600 mb-6 min-h-[40px]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{plan.blurb}</p>
                <div className="mb-7">
                  <span className="text-5xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>${plan.price.toFixed(0)}</span>
                  <span className="text-sm text-slate-500 ml-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>/ {plan.interval}</span>
                </div>
                <ul className="space-y-3 mb-8 flex-1">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-slate-700" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                      <CheckCircle2 className="w-4 h-4 text-[#34C759] flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                      {f}
                    </li>
                  ))}
                </ul>
                <button
                  data-testid={`landing-plan-cta-${plan.id}`}
                  onClick={handlePlanCta}
                  className={`w-full py-3 rounded-md font-semibold transition-all duration-200 ${plan.popular ? 'bg-[#002FA7] hover:bg-[#0034BD] text-white' : 'border border-slate-300 text-slate-900 hover:border-[#002FA7] hover:text-[#002FA7]'}`}
                  style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                >
                  {user ? 'Choose plan' : 'Start with this plan'}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ START TRIAL ============ */}
      <section id="start-trial" className="py-24 sm:py-32 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#002FA7] mb-4" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              Self-serve onboarding
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-6" style={{ fontFamily: 'Work Sans, sans-serif' }}>
              Create your company in under a minute
            </h2>
            <p className="text-lg text-slate-600 mb-8" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              No sales call, no approval queue. Enter your USDOT number and we pre-fill your carrier profile from FMCSA;
              your 14-day free trial starts the moment you sign up.
            </p>
            <ul className="space-y-3 mb-10" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              {['14 days free — no credit card required', 'Invite dispatchers and drivers right away', 'Keep your data when the trial ends; pick a plan any time'].map((t) => (
                <li key={t} className="flex items-start gap-3 text-slate-700"><CheckCircle2 className="w-5 h-5 text-[#002FA7] mt-0.5 shrink-0" strokeWidth={2} /> {t}</li>
              ))}
            </ul>
            <div className="flex flex-wrap items-center gap-4">
              <Link to="/start-trial" data-testid="section-cta-trial" className="group inline-flex items-center gap-2 px-7 py-3.5 bg-[#002FA7] hover:bg-[#0034BD] text-white rounded-md font-semibold shadow-lg shadow-[#002FA7]/30 transition-all duration-200" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                Start free trial <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={2.5} />
              </Link>
              <button type="button" onClick={startDemo} disabled={demoLoading} data-testid="section-cta-demo" className="inline-flex items-center gap-2 px-7 py-3.5 border border-slate-300 text-slate-800 hover:bg-white rounded-md font-semibold transition-colors disabled:opacity-60" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                {demoLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" strokeWidth={2} />} Try live demo
              </button>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm" data-testid="trial-steps-card">
            <h3 className="text-lg font-bold text-slate-900 mb-6" style={{ fontFamily: 'Work Sans, sans-serif' }}>Three steps to your first dispatch</h3>
            <ol className="space-y-5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              {[['Create your company', 'Company name, USDOT (optional), your admin login.'], ['Invite your team', 'Drivers onboard from their phone; dispatchers get role-based access.'], ['Dispatch & pay', 'Book loads, track live, run timezone-aware payroll with escrow and deductions.']].map(([h, d], i) => (
                <li key={h} className="flex gap-4"><span className="w-8 h-8 rounded-full bg-[#002FA7] text-white text-sm font-bold flex items-center justify-center shrink-0">{i + 1}</span><div><div className="font-semibold text-slate-900">{h}</div><div className="text-sm text-slate-600">{d}</div></div></li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ============ CTA ============ */}
      <section className="py-24 sm:py-32 bg-[#04081f] relative overflow-hidden">
        <div className="absolute inset-0 opacity-30" style={{
          backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(61,125,255,0.25), transparent 50%), radial-gradient(circle at 80% 70%, rgba(0,47,167,0.3), transparent 50%)',
        }} />
        <div className="relative max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="text-4xl sm:text-5xl font-bold tracking-tight text-white mb-6 leading-[1.1]" style={{ fontFamily: 'Work Sans, sans-serif' }}>
            Ready to take your fleet
            <br />from chaos to clarity?
          </h2>
          <p className="text-lg text-slate-300 mb-10 max-w-xl mx-auto" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            Set up your company in under 60 seconds. No credit card. No setup fees.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/start-trial"
              data-testid="cta-signup"
              className="group inline-flex items-center gap-2 px-8 py-4 bg-white text-[#04081f] hover:bg-slate-100 rounded-md font-semibold transition-all duration-200"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              Create your company
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={2.5} />
            </Link>
            <Link
              to="/login"
              data-testid="cta-signin"
              className="inline-flex items-center gap-2 px-8 py-4 border border-white/25 hover:border-white/50 text-white rounded-md font-semibold transition-all duration-200"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              I already have an account
            </Link>
          </div>
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer className="bg-[#04081f] border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img
              src="/icons/logo-wordmark.png"
              alt="CivilX TMS"
              className="h-9 w-auto opacity-90"
            />
          </div>
          <p className="text-xs text-slate-400" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            © {new Date().getFullYear()} CivilX TMS. Smarter loads. Stronger business.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
